import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService, PostLoginPhase } from '../../services/auth.service';

/**
 * Shared UI component for the post-login flow status.
 * Subscribes to AuthService streams and renders a step-by-step progress indicator.
 * Used by both AuthCallbackComponent (full-page overlay) and
 * AuthTestComponent (inline banner during OAuth simulation).
 *
 * Accessibility: uses role="status" + aria-live="polite" so screen readers
 * announce step changes without interrupting the user.
 */
@Component({
  selector: 'app-post-login-status',
  standalone: true,
  template: `
    @if (phase() || denialReason()) {
      <div
        class="pls-container"
        role="status"
        aria-live="polite"
        aria-atomic="false"
        aria-label="Stato del processo di autenticazione"
      >
        <!-- Steps -->
        @if (phase()) {
          <ol class="pls-steps" aria-label="Fasi di autenticazione">

            <!-- Step 1: Verifica profilo -->
            <li
              class="pls-step"
              [class.pls-step--active]="phase()!.phase === 'verifica'"
              [class.pls-step--done]="isDone('verifica')"
              [attr.aria-current]="phase()!.phase === 'verifica' ? 'step' : null"
            >
              <span class="pls-step__icon" aria-hidden="true">
                @if (isDone('verifica')) { ✓ }
                @else if (phase()!.phase === 'verifica') {
                  <span class="pls-spinner"></span>
                }
                @else { 1 }
              </span>
              <span class="pls-step__label">Verifica profilo</span>
            </li>

            <!-- Step 2: Caricamento permessi -->
            <li
              class="pls-step"
              [class.pls-step--active]="phase()!.phase === 'attesa'"
              [class.pls-step--done]="isDone('attesa')"
              [attr.aria-current]="phase()!.phase === 'attesa' ? 'step' : null"
            >
              <span class="pls-step__icon" aria-hidden="true">
                @if (isDone('attesa')) { ✓ }
                @else if (phase()!.phase === 'attesa') {
                  <span class="pls-spinner"></span>
                }
                @else { 2 }
              </span>
              <span class="pls-step__label">
                Caricamento permessi
                @if (phase()!.phase === 'attesa') {
                  <span class="pls-countdown" aria-label="tempo rimanente">
                    {{ countdown() }}s
                  </span>
                }
              </span>
            </li>

            <!-- Step 3: Finalizzazione -->
            <li
              class="pls-step"
              [class.pls-step--active]="phase()!.phase === 'finalizzazione'"
              [class.pls-step--done]="isDone('finalizzazione')"
              [attr.aria-current]="phase()!.phase === 'finalizzazione' ? 'step' : null"
            >
              <span class="pls-step__icon" aria-hidden="true">
                @if (isDone('finalizzazione')) { ✓ }
                @else if (phase()!.phase === 'finalizzazione') {
                  <span class="pls-spinner"></span>
                }
                @else { 3 }
              </span>
              <span class="pls-step__label">Finalizzazione accesso</span>
            </li>
          </ol>

          <!-- Progress bar (attesa only) -->
          @if (phase()!.phase === 'attesa') {
            <div class="pls-progress" aria-hidden="true">
              <div
                class="pls-progress__bar"
                [style.width.%]="progressPercent()"
              ></div>
            </div>
          }
        }

        <!-- Error banner -->
        @if (denialReason()) {
          <div
            class="pls-error"
            role="alert"
            aria-live="assertive"
          >
            <span class="pls-error__icon" aria-hidden="true">✕</span>
            <div>
              <strong class="pls-error__title">Accesso non consentito</strong>
              <p class="pls-error__detail">{{ denialReason() }}</p>
            </div>
          </div>
        }
      </div>
    }
  `,
  styles: [`
    .pls-container {
      background: #f0f7ff;
      border: 1px solid #c3d9f5;
      border-radius: 10px;
      padding: 1.25rem 1.5rem;
      font-family: inherit;
    }

    /* ── Steps ───────────────────────────────────── */
    .pls-steps {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.875rem;
    }

    .pls-step {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      opacity: 0.4;
      transition: opacity 0.3s;
    }

    .pls-step--active,
    .pls-step--done {
      opacity: 1;
    }

    .pls-step__icon {
      width: 1.75rem;
      height: 1.75rem;
      border-radius: 50%;
      background: #cbd5e1;
      color: #475569;
      font-size: 0.75rem;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: background 0.3s, color 0.3s;
    }

    .pls-step--active .pls-step__icon {
      background: #2563eb;
      color: white;
    }

    .pls-step--done .pls-step__icon {
      background: #16a34a;
      color: white;
    }

    .pls-step__label {
      font-size: 0.9rem;
      color: #334155;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .pls-step--active .pls-step__label {
      color: #1e3a8a;
      font-weight: 600;
    }

    .pls-step--done .pls-step__label {
      color: #166534;
    }

    /* ── Spinner ─────────────────────────────────── */
    .pls-spinner {
      display: inline-block;
      width: 0.9rem;
      height: 0.9rem;
      border: 2px solid rgba(255,255,255,0.4);
      border-top-color: white;
      border-radius: 50%;
      animation: pls-spin 0.7s linear infinite;
    }

    @keyframes pls-spin {
      to { transform: rotate(360deg); }
    }

    /* ── Countdown badge ─────────────────────────── */
    .pls-countdown {
      background: #dbeafe;
      color: #1d4ed8;
      font-size: 0.75rem;
      font-weight: 700;
      padding: 0.1rem 0.5rem;
      border-radius: 999px;
      min-width: 2.5rem;
      text-align: center;
    }

    /* ── Progress bar ────────────────────────────── */
    .pls-progress {
      margin-top: 1rem;
      height: 4px;
      background: #dbeafe;
      border-radius: 999px;
      overflow: hidden;
    }

    .pls-progress__bar {
      height: 100%;
      background: #2563eb;
      border-radius: 999px;
      transition: width 1s linear;
    }

    /* ── Error banner ────────────────────────────── */
    .pls-error {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      margin-top: 1rem;
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      padding: 1rem;
    }

    .pls-error__icon {
      width: 1.5rem;
      height: 1.5rem;
      background: #dc2626;
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.7rem;
      font-weight: 700;
      flex-shrink: 0;
    }

    .pls-error__title {
      display: block;
      font-size: 0.9rem;
      color: #991b1b;
      margin-bottom: 0.25rem;
    }

    .pls-error__detail {
      margin: 0;
      font-size: 0.825rem;
      color: #7f1d1d;
      line-height: 1.4;
    }
  `],
})
export class PostLoginStatusComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);

  phase = signal<PostLoginPhase | null>(null);
  denialReason = signal<string | null>(null);

  // Max countdown (3s) — doc integrazione CIAM: propagazione gruppo Azure AD in 2-3 secondi
  private readonly MAX_SECONDS = 3;

  /** Seconds remaining when phase === 'attesa', otherwise 0 */
  countdown = computed(() => {
    const p = this.phase();
    return p?.phase === 'attesa' ? p.countdown : 0;
  });

  /** Progress bar width % (grows as countdown decreases) */
  progressPercent = computed(() => {
    const p = this.phase();
    if (p?.phase !== 'attesa') return 0;
    return Math.round(((this.MAX_SECONDS - p.countdown) / this.MAX_SECONDS) * 100);
  });

  /**
   * Returns true when a given step is "done" (i.e. we've moved past it).
   * Step order: verifica → attesa → finalizzazione
   */
  isDone(step: 'verifica' | 'attesa' | 'finalizzazione'): boolean {
    const order: PostLoginPhase['phase'][] = ['verifica', 'attesa', 'finalizzazione', 'negato'];
    const current = this.phase()?.phase;
    if (!current) return false;
    return order.indexOf(current) > order.indexOf(step);
  }

  private subs = new Subscription();

  ngOnInit(): void {
    this.subs.add(
      this.authService.postLoginStep$.subscribe(p => this.phase.set(p)),
    );
    this.subs.add(
      this.authService.accessDenialReason$.subscribe(r => this.denialReason.set(r)),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
